'use client'

import { useCallback, useEffect, useRef } from 'react'
import { useAccount, usePublicClient, useBlockNumber } from 'wagmi'
import type { Address } from 'viem'
import { useSubaccountStore } from './useSubaccountStore'
import { getSubAccountAddress } from '../shared/subAccounts'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { E_LEND_CHAIN_ID, EVC_ADDRESS } from '@/config/euler-contracts'

/** Number of subaccounts to query per batch */
const BATCH_SIZE = 32

/** Re-discover after this many blocks */
const STALE_BLOCKS = 100

export interface UseSubaccountDiscoveryResult {
  /** Trigger a manual discovery */
  discover: () => Promise<void>
  /** Whether discovery is in progress */
  isDiscovering: boolean
  /** List of active subaccount IDs */
  activeSubaccountIds: number[]
  /** Whether the cache is stale and needs refresh */
  isStale: boolean
}

/**
 * Hook that discovers active subaccounts on wallet connect and keeps the cache fresh.
 *
 * Scans all 256 possible subaccounts using batched multicalls:
 * - Queries controllers in batches of 32 (8 RPC calls total)
 * - Guarantees finding all positions regardless of subaccount ID
 */
export function useSubaccountDiscovery(): UseSubaccountDiscoveryResult {
  const { address: owner } = useAccount()
  const publicClient = usePublicClient({ chainId: E_LEND_CHAIN_ID })
  const { data: blockNumber } = useBlockNumber({
    chainId: E_LEND_CHAIN_ID,
    watch: false,
  })

  const {
    setOwner,
    setDiscoveryResult,
    setDiscoveryFailed,
    setIsDiscovering,
    lastDiscoveryBlock,
    isDiscovering,
    activeSubaccountIds,
  } = useSubaccountStore()

  // Track if discovery is already running and for which owner
  const discoveryInProgress = useRef(false)
  const discoveryOwnerRef = useRef<Address | null>(null)

  // Update owner when wallet changes and reset discovery state for the new owner
  useEffect(() => {
    setOwner(owner ?? null)
    // If owner changed and discovery was in progress for a different owner,
    // allow new discovery to start (the old one will discard its results anyway)
    if (discoveryOwnerRef.current && owner && discoveryOwnerRef.current.toLowerCase() !== owner.toLowerCase()) {
      discoveryInProgress.current = false
      discoveryOwnerRef.current = null
    }
  }, [owner, setOwner])

  const discover = useCallback(async () => {
    if (!owner || !publicClient || discoveryInProgress.current) {
      return
    }

    // Capture owner at start to detect wallet changes during discovery
    const discoveryOwner = owner

    discoveryInProgress.current = true
    discoveryOwnerRef.current = discoveryOwner
    setIsDiscovering(true)

    try {
      const active: number[] = []

      // Scan all 256 subaccounts in batches
      for (let batchStart = 0; batchStart < 256; batchStart += BATCH_SIZE) {
        const batchSize = Math.min(BATCH_SIZE, 256 - batchStart)

        // Build multicall contracts for this batch
        const contracts = Array.from({ length: batchSize }, (_, i) => ({
          address: EVC_ADDRESS as Address,
          abi: eulerVaultConnectorAbi,
          functionName: 'getControllers' as const,
          args: [getSubAccountAddress(discoveryOwner, batchStart + i)] as const,
        }))

        const results = await publicClient.multicall({ contracts })

        results.forEach((result, i) => {
          if (result.status === 'success') {
            const controllers = result.result as Address[]
            if (controllers.length > 0) {
              active.push(batchStart + i)
            }
          }
        })
      }

      // Check if owner changed during discovery - if so, discard results
      const currentOwner = useSubaccountStore.getState().owner
      if (currentOwner?.toLowerCase() !== discoveryOwner.toLowerCase()) {
        return
      }

      const currentBlock = await publicClient.getBlockNumber()
      setDiscoveryResult(active, Number(currentBlock))
    } catch {
      // Check if owner changed - if so, don't mark as failed for the new owner
      const currentOwner = useSubaccountStore.getState().owner
      if (currentOwner?.toLowerCase() !== discoveryOwner.toLowerCase()) {
        return
      }
      // Mark discovery as attempted to prevent retry spam
      // Use current block if available, otherwise use a large number that will become stale after STALE_BLOCKS
      const fallbackBlock = blockNumber ? Number(blockNumber) : Math.floor(Date.now() / 12000) // ~12s per block
      setDiscoveryFailed(fallbackBlock)
    } finally {
      discoveryInProgress.current = false
      discoveryOwnerRef.current = null
    }
  }, [owner, publicClient, blockNumber, setDiscoveryResult, setDiscoveryFailed, setIsDiscovering])

  // Calculate if cache is stale
  const isStale = !!blockNumber && (lastDiscoveryBlock === 0 || Number(blockNumber) - lastDiscoveryBlock > STALE_BLOCKS)

  // Auto-discover on mount or when stale
  useEffect(() => {
    if (!owner || !publicClient) return

    const shouldDiscover =
      // Never discovered for this wallet
      lastDiscoveryBlock === 0 ||
      // Cache is stale
      isStale

    if (shouldDiscover && !isDiscovering && !discoveryInProgress.current) {
      discover()
    }
  }, [owner, publicClient, lastDiscoveryBlock, isStale, isDiscovering, discover])

  return {
    discover,
    isDiscovering,
    activeSubaccountIds,
    isStale,
  }
}
